import React from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Helper to format amount - auto convert to Cr when >= 100L
const formatAmount = (lakhs) => {
    if (lakhs >= 100) {
        const cr = lakhs / 100;
        return `₹${cr % 1 === 0 ? cr : cr.toFixed(1)}Cr`;
    }
    return `₹${lakhs}L`;
};

const TOTAL_FUNDING = 20; // ₹7 Cr in Lakhs

const data = [
    {
        name: 'AI Infra',
        value: 30,
        color: '#009900',
        points: [
            { label: 'GPU Compute Clusters', cost: 4.8 },
            { label: 'Model Training Pipeline', cost: 0.2 },
            { label: 'Network & Security', cost: 1 },
        ]
    },
    {
        name: 'Compliance',
        value: 50,
        color: '#FFFFFF',
        points: [
            { label: 'Total RIA Regulatory Fee Base', cost: 8.25 },
            { label: 'AMFI ARN Fee for Pvt Ltd ', cost: .5 },
            { label: 'other licences & registrations', cost: 1.25 },
        ]
    },

    {
        name: 'Growth',
        value: 20,
        color: '#666666',
        points: [
            { label: 'Content & community-led acquisition', cost: 1.5 },
            { label: 'User research, pilots & surveys', cost: 1.5 },
            { label: 'Analytics & feedback tools', cost: 1 },
        ]
    },
    //{
    //    name: 'Ops',
    //    value: 14,
    //    color: '#333333',
    //    points: [
    //        { label: 'Office Infrastructure', cost: 20 },
    //        { label: 'Compliance Associate', cost: 30 },
    //        { label: 'Software, accounting & admin tools', cost: 5 },
    //    ]
    //}
];

const Slide15Ask = () => {
    return (
        <div className="w-full h-full bg-background p-8 lg:p-12 flex flex-col items-center justify-center relative overflow-hidden">
            {/* Background Grid Texture */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
            />
            <div className="flex flex-col lg:flex-row items-center w-full max-w-7xl gap-8 lg:gap-16">

                {/* Left Side: The Ask Text */}
                <div className="w-full lg:w-2/5 text-left shrink-0">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <h2 className="text-5xl lg:text-7xl font-bold mb-4">The Ask</h2>
                        <p className="text-6xl lg:text-8xl font-bold text-primary mb-8 tracking-tighter">₹ 20 Lakhs</p>

                        <div className="space-y-4 mb-8">
                            <div className="text-2xl text-white">for <span className="font-bold">3 Months</span> Runway</div>
                            <p className="text-muted-foreground text-lg">
                                Goal: Market entry, Regulatory readiness, Early scale.
                            </p>
                        </div>

                        <div className="flex gap-4">
                            <div className="px-6 py-3 border border-primary rounded-lg text-primary font-bold">
                                Seed Stage
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Right Side: Chart & Cards */}
                <div className="w-full lg:w-3/5 flex flex-col items-center gap-6">

                    {/* Pie Chart */}
                    <div className="w-full h-[250px] lg:h-[300px] relative shrink-0">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                            className="w-full h-full"
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={data}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={120}
                                        paddingAngle={5}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {data.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#111', borderColor: '#333', borderRadius: '8px', color: '#fff' }}
                                        itemStyle={{ color: '#fff' }}
                                        formatter={(value) => `${value}%`}
                                    />
                                </PieChart>
                            </ResponsiveContainer>

                            {/* Center Text in Donut */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                                <span className="block text-xs text-muted-foreground uppercase tracking-widest">Allocation</span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Cards Grid - All cards fit content */}
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {data.map((item, index) => {
                            const total = item.points.reduce((sum, p) => sum + p.cost, 0);
                            const pointCount = item.points.length;

                            // Determine if card needs extra width
                            const isWide = pointCount >= 5;

                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 + (index * 0.1) }}
                                    className={`
                                        bg-white/5 border border-white/10 rounded-xl flex flex-col backdrop-blur-sm hover:bg-white/10 transition-colors
                                        p-5 gap-4
                                        ${isWide ? 'lg:col-span-2' : ''}
                                    `}
                                >
                                    {/* Card Header */}
                                    <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                                            <span className="font-bold text-white text-base">{item.name}</span>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <span className="text-xl font-bold text-white tabular-nums">{item.value}%</span>
                                            <span className="block text-sm text-muted-foreground">{formatAmount(total)}</span>
                                        </div>
                                    </div>
                                    {/* Points List - Generous spacing */}
                                    <ul className="space-y-3 flex-1">
                                        {item.points.map((point, i) => (
                                            <li key={i} className="text-base text-white/80 flex items-center justify-between gap-4">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-2 h-2 rounded-full bg-white/40 shrink-0" />
                                                    <span>{point.label}</span>
                                                </div>
                                                <span className="text-base font-medium text-white/70 tabular-nums shrink-0">{formatAmount(point.cost)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Slide15Ask;